// @ts-strict-ignore
import { Message, MessageAdditionalContent, MessageID, MessageSide, MessageStatus, Reaction } from "rainbow-web-sdk/models/message.model";
import { RBEvent } from "rainbow-web-sdk/models/event.model";
import { Contact } from 'rainbow-web-sdk/models/contact.model';
import { ConversationRB, ConversationStatus, ConversationType } from 'rainbow-web-sdk/models/conversation.model';
import xmlParser, { XMLElement, XMLElements } from "rainbow-web-sdk/utils/xmlParser";

import { MainService } from 'rainbow-web-sdk/services/core/main.service';
import { XmppService } from 'rainbow-web-sdk/services/core/xmpp.service';
import { ContactService } from 'rainbow-web-sdk/services/core/contact.service';
import { LoggerService } from 'rainbow-web-sdk/services/core/logger.service';
import { UrlMetaData } from "rainbow-web-sdk/services/ulrMetadata/urlMetadata.service";
import { ConversationServiceEvents, ConversationServiceInnerEvents, ConversationServiceRB } from "rainbow-web-sdk/services/conversation/conversation.service";
import { EventService } from 'rainbow-web-sdk/services/core/event.service';

import type { WebinarServiceRB } from 'rainbow-web-sdk/services/webinars/webinars.service';
import { WEBINAR_SVC } from 'rainbow-web-sdk/services/webinars/webinars.service';

import { FileStorageService, FILE_STORAGE_SVC } from 'rainbow-web-sdk/services/files/fileStorage.service';

import type { FileServerService } from 'rainbow-web-sdk/services/files/fileServer.service';
import { FILE_SERVER_SVC } from 'rainbow-web-sdk/services/files/fileServer.service';

import type { WebrtcGatewayService } from "rainbow-web-sdk/services/webrtcGateway/webrtcGateway.service";
import { WEBRTC_GATEWAY_SVC } from "rainbow-web-sdk/services/webrtcGateway/webrtcGateway.service";

import type { BubbleConferenceServiceRB } from "rainbow-web-sdk/services/conference/bubbleConference.service";
import { BUBBLE_CONFERENCE_SVC } from "rainbow-web-sdk/services/conference/bubbleConference.service";

import type { BubbleServiceRB } from 'rainbow-web-sdk/services/bubbles/bubble.service';
import { BUBBLE_SVC } from 'rainbow-web-sdk/services/bubbles/bubble.service';
import { BubbleUserStatus } from "rainbow-web-sdk/models";
import { CallStatus } from "rainbow-web-sdk/models/call.model";
import { I18nService } from "../core/i18n.service";
import { stringifyError } from "../../utils/stringifyError";
import { formatDuration, intervalToDuration } from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import { SettingsService } from "rainbow-web-sdk/services/core/settings.service";

export class ConversationServiceEventHandler {

    public static create(): ConversationServiceEventHandler {
        return new ConversationServiceEventHandler();
    }

    private xmppService: XmppService = XmppService.getInstance();
    private logger: LoggerService = LoggerService.getInstance();
    private mainService: MainService = MainService.getInstance();
    private contactService: ContactService = ContactService.getInstance();
    private eventService: EventService = EventService.getInstance();
    private conversationService: ConversationServiceRB = ConversationServiceRB.getInstance();
    private i18n: I18nService = I18nService.getInstance();
    private settingsService: SettingsService = SettingsService.getInstance();

    private webConferenceService?: BubbleConferenceServiceRB = undefined;
    private webinarService?: WebinarServiceRB = undefined;
    private fileServerService?: FileServerService = undefined;
    private fileStorageService?: FileStorageService = undefined;
    private webrtcGatewayService?: WebrtcGatewayService = undefined;
    private roomService?: BubbleServiceRB = undefined;

    private constructor() {
        this.webConferenceService = this.mainService.inject<BubbleConferenceServiceRB>(BUBBLE_CONFERENCE_SVC);
        this.webinarService = this.mainService.inject<WebinarServiceRB>(WEBINAR_SVC);
        this.fileServerService = this.mainService.inject<FileServerService>(FILE_SERVER_SVC);
        this.fileStorageService = this.mainService.inject<FileStorageService>(FILE_STORAGE_SVC);
        this.webrtcGatewayService = this.mainService.inject<WebrtcGatewayService>(WEBRTC_GATEWAY_SVC);
        this.roomService = this.mainService.inject<BubbleServiceRB>(BUBBLE_SVC);
    }

    public onChatMessageReceived(stanza: any): boolean {
        try {
            const stanzaElem = xmlParser(stanza);

            if (stanzaElem.find("event").length > 0 || stanzaElem.find("propose").length > 0 || stanzaElem.find("received").length > 0) {
                return true;
            }

            if (stanzaElem.find("recording").length > 0) { // intercept recording msg
                this.logger.info("[ConversationServiceEventHandler] onChatMessageReceived : recording msg case");
                this.onRecordingMessageReceived(stanza);
            }
            else if (stanzaElem.find("record").length > 0) { // intercept recording msg in conference
                //ignore this message as this is for conf V1
            }
            else {
                this.extractStanzaData(stanza)
                    .then((stanzaData: any) => {
                        if (stanzaData) {
                            if (stanzaData.body || stanzaData.additionalContent || stanzaData.oob || stanzaData.conference || stanzaData.replace || stanzaData.modified || stanzaData.reaction) {
                                this.logger.info("[ConversationServiceEventHandler] onChatMessageReceived");
                                this.handleChatMessage(stanzaData);
                            }
                            else if (!stanzaData.outgoingMessage && !this.contactService.isUserContact(stanzaData.participant) && stanzaData.status) {
                                this.handleStatusMessage(stanzaData.conversation, stanzaData.participant, stanza);
                            }
                            else if (stanzaData.pin) {
                                this.handlePinMessage(stanzaData);
                            }
                            else if (stanzaData.recap) {
                                this.handleRecapShareMessage(stanzaData);
                            }
                        }
                    });
            }
            return true;
        }
        catch (error) {
            this.logger.error("[ConversationServiceEventHandler] onChatMessageReceived ERROR " + error);
            return true;
        }
    }

    public async onErrorMessageReceived(stanza: any): Promise<void> {
        try {
            const stanzaData = await this.extractStanzaData(stanza);
            const message: Message = stanzaData.conversation.getMessageById(stanzaData.messageId);
            message.sendStatus = MessageStatus.ERROR;
            message.sendUpdateEvent();
            this.logger.info("[ConversationServiceEventHandler] onErrorMessageReceived");
        }
        catch (error) {
            this.logger.error("[ConversationServiceEventHandler] onErrorMessageReceived ERROR " + error);
        }
    }

    public onConferenceMessageReceived(stanza: any): boolean {
        this.logger.info("[ConversationServiceEventHandler] onConferenceMessageReceived");
        try {
            const stanzaElem = xmlParser(stanza);
            if (stanzaElem.find("event").length === 0) {
                this.logger.info("[ConversationServiceEventHandler] onConferenceMessageReceived ignore the message");
            }
            else {
                this.extractStanzaData(stanza).then((stanzaData: any) => {
                    if (stanzaData.conference) { this.handleChatMessage(stanzaData); }
                });
            }
            return true;
        }
        catch (error) {
            this.logger.error("[ConversationServiceEventHandler] onConferenceMessageReceived ERROR " + error);
            return true;
        }
    }

    public onRoomAdminMessageReceived(conversation: ConversationRB, contact: Contact, type: string, msgId: MessageID, translatedMessage: any): void {

        if (type === 'StageUpdate') return;
        if (type === 'pollVote' || type === 'pollDelete') return;
        this.logger.info(`[ConversationServiceEventHandler] onRoomAdminMessageReceived of type: ${type}`);

        const message = this.conversationService.addAdminBubbleMessage(conversation, contact, new Date(), type, msgId, translatedMessage);
        this.conversationService.setStatusMessage(conversation, contact, ConversationStatus.ACTIVE);

        this.conversationService.sendAckReceivedMessage(conversation, message);

        conversation.setMissedCounter(conversation.missedCounter + 1);
        this.conversationService.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_CONVERSATION_AUTO_ACK_CHECK, { conversation, message });

        // Send conversations update event
        // this.eventService.publish("ON_CONVERSATION_MESSAGE_RECEIVED", message, conversation, false);
        // this.eventService.publish("ON_CONVERSATIONS_UPDATED_EVENT", conversation, Conversation.EventType.IM);
    }

    public onFileMessageReceived(stanza: any): boolean {
        this.logger.info("[ConversationServiceEventHandler] onFileMessageReceived");
        try {
            this.extractStanzaData(stanza)
                .then((stanzaData: any) => {
                    if (stanzaData.body) {
                        const conversation: ConversationRB = stanzaData.conversation;
                        this.conversationService.addFileMessage(conversation, stanzaData.participant, stanzaData.date, stanzaData.body, stanzaData.messageId, stanzaData.urgency);
                        this.conversationService.setStatusMessage(conversation, stanzaData.participant, ConversationStatus.ACTIVE);
                        // this.eventService.publish("ON_CONVERSATIONS_UPDATED_EVENT", conversation, Conversation.EventType.FILE);
                    }
                });
            return true;
        }
        catch (error) {
            this.logger.error("[ConversationServiceEventHandler] onFileMessageReceived ERROR " + error);
            return true;
        }
    }

    public onWebRTCMessageReceived(stanza: any): boolean {
        this.logger.info("[ConversationServiceEventHandler] onWebRTCMessageReceived");
        try {
            // Treat new call_log messages
            if (xmlParser(stanza).find("call_log").length > 0) {
                this.extractWebrtcStanzaData(stanza)
                    .then((stanzaData: any) => {
                        if (stanzaData?.body) {
                            const conversation: ConversationRB = stanzaData.conversation;
                            const message = this.conversationService.addWebRTCMessage(conversation, stanzaData.participant, stanzaData.date, stanzaData.body, stanzaData.messageId);
                            this.conversationService.setStatusMessage(conversation, stanzaData.participant, ConversationStatus.ACTIVE);
                            this.conversationService.sendAckReceivedMessage(conversation, message);

                            if (message.side === MessageSide.RIGHT || (stanzaData.state && stanzaData.state === "answered")) {
                                conversation.ackReadMessage(message);
                            }
                            else {
                                conversation.setMissedCounter(conversation.missedCounter + 1);
                                this.conversationService.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_CONVERSATION_AUTO_ACK_CHECK, { conversation, message });
                            }

                            // this.eventService.publish("ON_CONVERSATIONS_UPDATED_EVENT", conversation);
                        }
                    });
            }
            // Compatibility with old messages
            else {
                this.extractStanzaData(stanza)
                    .then((stanzaData: any) => {
                        if (stanzaData?.body) {
                            const conversation: ConversationRB = stanzaData.conversation;
                            this.conversationService.addWebRTCMessage(conversation, stanzaData.participant, stanzaData.date, stanzaData.body, stanzaData.messageId);
                            this.conversationService.setStatusMessage(conversation, stanzaData.participant, ConversationStatus.ACTIVE);
                            // this.eventService.publish("ON_CONVERSATIONS_UPDATED_EVENT", conversation);
                        }
                    });
            }
            return true;
        }
        catch (error) {
            this.logger.error("[ConversationServiceEventHandler] onWebRTCMessageReceived error " + error);
            return true;
        }
    }

    public onPBXMessageReceived(stanza: any): boolean {
        this.logger.info("[ConversationServiceEventHandler] onWebRTCMessageReceived");
        try {
            // Treat new call_log messages
            if (xmlParser(stanza).find("call_log").length > 0) {
                this.extractWebrtcStanzaData(stanza)
                    .then((stanzaData: any) => {
                        if (stanzaData?.body) {
                            const conversation: ConversationRB = stanzaData.conversation;
                            const message = this.conversationService.addWebRTCMessage(conversation, stanzaData.participant, stanzaData.date, stanzaData.body, stanzaData.messageId);
                            this.conversationService.setStatusMessage(conversation, stanzaData.participant, ConversationStatus.ACTIVE);
                            this.conversationService.sendAckReceivedMessage(conversation, message);

                            if (message.side === MessageSide.RIGHT || (stanzaData.state && stanzaData.state === "answered")) {
                                conversation.ackReadMessage(message);
                            }
                            else {
                                conversation.setMissedCounter(conversation.missedCounter + 1);
                                this.conversationService.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_CONVERSATION_AUTO_ACK_CHECK, { conversation, message });
                            }
                            // this.eventService.publish("ON_CONVERSATIONS_UPDATED_EVENT", conversation);
                        }
                    });
            }
            return true;
        }
        catch (error) {
            this.logger.error("[ConversationServiceEventHandler] onWebRTCMessageReceived error " + error);
            return true;
        }
    }

    public onManagementMessageReceived(stanza: any): boolean {
        try {
            const stanzaElem = xmlParser(stanza);
            const conversationElem = stanzaElem.find("conversation");
            const clearDoneElem = stanzaElem.find("clear_done");
            if (conversationElem) {
                const action = conversationElem.attr("action");
                let conversationGetterPromise = null;
                if (action === "create") {
                    //if the event is coming from server, do not set the dbID so that we can get the infos from the server via API
                    const jid = this.xmppService.getBareJidFromJid(conversationElem.find("peer").text());
                    const convType = conversationElem.find("type").text();
                    // const dbId = conversationElem.attr("id");
                    if (convType === "bot") { conversationGetterPromise = this.conversationService.getOrCreateBotConversationInternal(jid); }
                    else {
                        conversationGetterPromise = this.conversationService[convType === 'room' ? 'getRoomConversationInternal' : 'getOrCreateOneToOneConversationInternal'](jid);
                    }
                }
                else {
                    conversationGetterPromise = Promise.resolve(this.conversationService.getConversationFromCache(conversationElem.attr("id")));
                }

                conversationGetterPromise.then((conversation: ConversationRB) => {
                    this.logger.info("[ConversationServiceEventHandler] onManagementMessageReceived (" + action + " conversation)");
                    if (conversation) {
                        switch (action) {
                            case "create": {
                                conversation.dbId = conversationElem.attr("id");
                                const lastMessageDate = conversationElem.find("lastMessageDate").text();

                                if (lastMessageDate && lastMessageDate.length > 0) {
                                    const lastMessageTimestamp = (new Date(lastMessageDate)).getTime();
                                    if (conversation.lastModification.getTime() < lastMessageTimestamp)
                                        conversation.lastModification = new Date(lastMessageDate);
                                }

                                conversation.missedCounter = parseInt(conversationElem.find("unreadMessageNumber").text(), 10) || 0;
                                conversation.muted = conversationElem.find("mute").text() === "true";
                                const lastMessageText = conversationElem.find("lastMessageText").text();
                                const lastMessageSender = conversationElem.find("lastMessageSender").text();
                                const isEvent = conversationElem.find("lastType").text() === "event";

                                conversation.lastMessage.text = lastMessageText;
                                conversation.lastMessage.sender = lastMessageSender;
                                if (isEvent) {

                                    const eventName = conversationElem.find("name").text();
                                    const eventJid = conversationElem.find("jid").text();
                                    if (eventName && eventJid) { conversation.lastMessage.sender = eventJid; }

                                    if (eventName === 'conferenceRemove') { conversation.lastMessage.text = 'conferenceRemoveMsgRoom'; }
                                    else if (eventName === 'conferenceAdd') { conversation.lastMessage.text = 'conferenceAddMsgRoom'; }
                                }
                                this.conversationService.sendEvent(ConversationServiceEvents.ON_CONVERSATION_CREATED, { conversation });

                            } break;
                            case "delete":
                                this.conversationService.removeLocalConversation(conversation);
                                break;
                            case "update": {
                                const bookmarkElem = conversationElem.find("bookmark");
                                if (bookmarkElem?.length) {
                                    const messageId = bookmarkElem.find("messageId").text() as MessageID;
                                    if (messageId) {
                                        const timestamp = Number(bookmarkElem.find("timestamp").text());
                                        const unreadMessageNumber = Number(bookmarkElem.find("unreadMessageNumber").text());
                                        conversation.bookmark = { messageId: messageId, timestamp: timestamp };
                                        conversation.setMissedCounter(unreadMessageNumber);
                                        this.logger.info(`[ConversationServiceEventHandler] onManagementMessageReceived - Bookmarked ${messageId} on ${timestamp} with ${unreadMessageNumber} unread`);
                                    } else {
                                        conversation.bookmark = null;
                                        this.logger.info(`[ConversationServiceEventHandler] onManagementMessageReceived - Bookmark removed`);
                                    }
                                }
                                break;
                            }
                            default: break;
                        }
                    }
                    else if (action === "create") {
                        const convId = this.xmppService.getBareJidFromJid(conversationElem.find("peer").text());
                        const convDbId = conversationElem.attr("id");
                        const lastModification = new Date(conversationElem.find("lastMessageDate").text());
                        const lastMessageText = conversationElem.find("lastMessageText").text();
                        const lastMessageSender = conversationElem.find("lastMessageSender").text();
                        const missedIMCounter = parseInt(conversationElem.find("unreadMessageNumber").text(), 10) || 0;
                        const muted = conversationElem.find("mute").text() === "true";
                        const type = conversationElem.find("type").text();

                        this.conversationService.getOrCreateConversation(convId, type)
                            .then((conversation: ConversationRB) => {
                                this.logger.info("[ConversationServiceEventHandler] onManagementMessageReceived update conversation (" + conversation.id + ")");
                                conversation.dbId = convDbId;
                                conversation.lastModification = lastModification ? new Date(lastModification) : undefined;
                                conversation.lastMessage.text = lastMessageText;
                                conversation.lastMessage.sender = lastMessageSender;
                                conversation.muted = muted;
                                conversation.setMissedCounter(missedIMCounter);
                                if (type === 'bot') { conversation.type = ConversationType.P2P; } // MCO BOT CHANGE
                                this.conversationService.sendEvent(ConversationServiceEvents.ON_CONVERSATION_CREATED, { conversation });
                            });
                    }
                });
            }

            // Clear done received to clear all message from conversation
            if (clearDoneElem.length) {
                const roomId = clearDoneElem.attr('with');
                const conv = this.conversationService.getConversationFromCache(roomId);

                if (conv) {
                    this.logger.info("[conversationServiceEventHandler] Remove all messages for conversation " + conv.id);
                    setTimeout(() => {
                        conv.reset();
                        this.eventService.publish("GLOBAL_SUCCESS_NOTIFY_MESSAGE_EVENT", this.i18n.translate('cleanupDone', { bubble: conv.bubble?.name }), 4000, 0, false, false, true);
                    }, 2000);
                }
                return true;
            }
            // Handle mute/unmute room
            if (stanzaElem.find("mute") || stanzaElem.find("unmute")) {
                const muteElem = stanzaElem.find("mute");
                let mute = false;
                if (muteElem.length) {
                    if (muteElem.text().length) { mute = (muteElem.text() === "true"); }
                    else { mute = true; }
                }
                const conversationDbId = stanzaElem.find("mute").attr("conversation") || stanzaElem.find("unmute").attr("conversation");
                const conversation = this.conversationService.getConversationFromCache(conversationDbId);
                if (conversation) {
                    this.logger.info("[ConversationServiceEventHandler] onManagementMessageReceived : mute is changed to " + mute);
                    conversation.muted = mute;
                }
            }
            return true;
        }
        catch (error) {
            this.logger.error("[ConversationServiceEventHandler] onManagementMessageReceived error " + error);
            return true;
        }
    }

    public onReceiptMessageReceived(stanza: any): boolean {
        try {
            // Get stanza element
            let stanzaElement = xmlParser(stanza);
            const messageSender = this.xmppService.getBareJidFromJid(stanzaElement.attr("from"));
            const carbonStanza = stanzaElement.find("message");
            if (this.contactService.isUserContactJid(messageSender) && carbonStanza.length > 0) { stanzaElement = carbonStanza.first(); }

            // Extract stanza data
            const recvMessage = stanzaElement.find("received");
            if (recvMessage.length && recvMessage.length > 0) {
                this.logger.info("[ConversationServiceEventHandler] onReceiptMessageReceived");

                const messageId = recvMessage.attr("id");
                const entity = recvMessage.attr("entity");
                const event = recvMessage.attr("event");

                // Handle server response
                if (entity === "server" && event === "received") {
                    const messageInfo = this.conversationService.pendingMessages[messageId];
                    if (messageInfo?.message) {
                        const message = messageInfo.message;
                        const conversation = messageInfo.conversation ? messageInfo.conversation : messageInfo.destConv;

                        this.logger.info("[conversationService] Receive server ack (" + conversation.id + ", " + message.id + ")");
                        //update the date with the received date from the server actually
                        if (stanzaElement.find("timestamp")?.length) {
                            //update the message date
                            message.date = new Date(stanzaElement.find("timestamp").attr("value"));
                        }
                        message.setReceiptStatus(MessageStatus.SENT);
                        delete this.conversationService.pendingMessages[messageId];

                        // this.eventService.publish("ON_CONVERSATION_RECEIPT_RECEIVED", message, conversation, "server");
                    }
                }
                else if (entity === "client") {
                    this.extractStanzaData(stanza)
                        .then((stanzaData: any) => {
                            this.handleReceiptReceiveMessage(stanzaData.conversation, stanza);
                        });
                }
            }
            else if (stanzaElement.find("mark_as_read").length) {
                this.logger.info("[ConversationServiceEventHandler] onReceiptMessageReceived -- mark_as_read");
                const conversationId = stanzaElement.find("mark_as_read").attr("with");
                const conversation = this.conversationService.getConversationFromCache(conversationId);
                if (conversation) {
                    const maskRead = stanzaElement.find("mark_as_read").attr("maskread") === "true";
                    switch (stanzaElement.find("mark_as_read").attr("id")) {
                        case "all-received": // messages for this conversation have been acknowledged
                            conversation.setMissedCounter(0);
                            this.conversationService.updateMissedCounters();
                            conversation.ackReadAllReceivedMessages();
                            //mark
                            break;
                        case "all-sent": // messages for this conversation have been read
                            if (maskRead) {
                                conversation.ackReceivedAllSentMessages();
                            }
                            else {
                                conversation.ackReadAllSentMessages();
                            }

                            break;
                        default:
                            this.logger.error("[ConversationServiceEventHandler] onReceiptMessageReceived error - unknown read type");
                            break;
                    }
                }
            }
            else if (stanzaElement.find("voicemessage").length) {
                this.logger.debug("[ConversationServiceEventHandler] onReceiptMessageReceived VoiceMessage detected");
            }

            return true;
        }
        catch (error) {
            this.logger.error("[ConversationServiceEventHandler] onReceiptMessageReceived error " + error);
            return true;
        }
    }

    /*********************************************************************/
    /** PRIVATE METHODS                                                 */
    /*********************************************************************/
    private extractStanzaData(stanza: any): Promise<any> {
        return new Promise((resolve, reject) => {

            let stanzaElem = xmlParser(stanza);
            const from = stanzaElem.attr("from");
            const messageSender = this.xmppService.getBareJidFromJid(from);
            const stanzaData: any = {};

            let results = null;

            // Handle carbon message
            const carbonStanza = stanzaElem.find('message');

            if (carbonStanza.length > 0 && this.contactService.isUserContactJid(messageSender)) {
                let origMsgId: MessageID;
                const forwardedCarbonMsg = stanzaElem.find('sent forwarded message forwarded message');
                if (forwardedCarbonMsg?.length) {
                    origMsgId = stanzaElem.find('sent forwarded message').attr('id') as MessageID;
                    stanzaElem = forwardedCarbonMsg.first();
                    stanzaData.isForwarded = true;
                }
                results = this.handleCarbonMsg(carbonStanza, stanzaData, stanzaElem, origMsgId);
            }
            else {
                let origMsgId: MessageID;
                const forwardedMsg = stanzaElem.find("forwarded");
                if (forwardedMsg?.length) {
                    origMsgId = stanzaElem.attr('id') as MessageID;
                    stanzaElem = forwardedMsg.first();
                    const fwdMessageElem = stanzaElem.find('message');
                    stanzaData.fwdMsgdInfo = {
                        id: fwdMessageElem.attr('id'),
                        from: this.xmppService.getBareJidFromJid(fwdMessageElem.attr('from')),
                        showIdentity: fwdMessageElem.find('show-identity').length > 0
                    };
                    stanzaData.isForwarded = true;
                }
                results = this.handleStandardMsg(stanzaData, stanzaElem, from, origMsgId, resolve);
            }

            // Handle common data
            if (results) {
                this.handleCommonData(stanza, stanzaData, stanzaElem, results.conversationId, results.delay, results.resource, resolve, reject);
            }
        });
    }

    private handleCommonData(stanza: any, stanzaData: any, stanzaElem: XMLElement, conversationId: string, delay: XMLElements, resource: string, resolve: any, reject: any): void {
        const archived = (stanzaElem.find("retransmission").attr("source") === "push");

        stanzaData.date = new Date();
        //get the right date from the stanza-id
        let microsec;
        if (stanzaElem.find('stanza-id')?.length && stanzaElem.find('stanza-id').attr('id')) {
            microsec = parseInt(stanzaElem.find('stanza-id').attr('id'), 10) / 1000;
        }
        //if not found, use archived
        else if (stanzaElem.find('archived')?.length && stanzaElem.find('archived').attr('id')) {
            microsec = parseInt(stanzaElem.find('archived').attr('id'), 10) / 1000;
        }

        stanzaData.offline = false;
        if (delay.text() === "Offline Storage") {
            stanzaData.date = new Date(delay.attr("stamp"));
            stanzaData.offline = true;
        }

        //TO DO DNI : The search of message should be done via API and not via MAM search as this does not always work ..
        //server send in microsec, we cannot manage that, so we transform it to milisec, and round down;
        //on reception side, when we do the search, we add 1 milisec so that we can get "before" value correctly
        if (microsec) {
            //round down
            stanzaData.date = new Date(Math.floor(microsec));
        }

        // Handle pushed messages
        if (archived) {
            let conversation = this.conversationService.getConversationFromCache(conversationId);
            if (!conversation) {
                this.conversationService.getOrCreateConversation(conversationId, conversationId.startsWith("room_") ? 'room' : 'contact')
                    .then((conv) => {
                        conversation = conv;
                        reject();
                    });
            }
            else { reject(); }
            return;
        }

        const existingConv = this.conversationService.getConversationFromCache(conversationId);
        stanzaData.convReady = existingConv?.dbId !== null;

        // Get or create the associated conversation only if body or existing conversation
        if (stanzaData.body || existingConv || (stanzaData.oob) || (stanzaData.conference)) {

            // Handle room conversation
            if (conversationId.indexOf("room_") === 0 && this.roomService) {
                const room = this.roomService.getBubbleFromCache(conversationId);
                const subject = stanzaElem.find("subject");
                const emptySubject = (subject.length && subject.text() === "");

                // Ignore message dediceted to un-accepted rooms
                if (room && room.bubbleConnectedUser.status !== BubbleUserStatus.ACCEPTED || emptySubject) {
                    this.logger.info("[conversationServiceEvent] extractStanzaData -- ignore this stanza message");
                }
                else {
                    this.conversationService.getOrCreateConversation(conversationId, 'room')
                        .then((roomConv) => {
                            //in case we create a new conversation, the missed counter from the server is already updated
                            //so we dont need to increase it (as the new message is added to the missed counter)
                            if (!existingConv && roomConv.missedCounter === 1) {
                                roomConv.missedCounter = 0;
                            }
                            stanzaData.conversation = roomConv;
                            if (stanzaData.conference?.ownerjid) {
                                return this.contactService.getOrCreateContact(stanzaData.conference.ownerjid);
                            }

                            if (resource && resource.indexOf("/") !== -1) {
                                resource = resource.split("/")[0];
                            }
                            return resource ? this.contactService.getOrCreateContact(resource) : null;
                        })
                        .then((contact: Contact) => {
                            stanzaData.participant = contact;
                            resolve(stanzaData);
                        })
                        .catch((error: any) => {
                            this.logger.error("[conversationServiceEventHandler] impossible to fetch conversation " + stringifyError(error));
                            reject();
                        });
                }
            } else {
                this.conversationService.getOrCreateConversation(conversationId, 'contact')
                    .then((conv) => {
                        //in case we create a new conversation, the missed counter from the server is already updated
                        //so we dont need to increase it (as the new message is added to the missed counter)
                        if (!existingConv && conv.missedCounter === 1) {
                            conv.missedCounter = 0;
                        }
                        stanzaData.conversation = conv;
                        if (!stanzaData.participant) { stanzaData.participant = conv.contact; }
                        resolve(stanzaData);
                    })
                    .catch((error) => {
                        this.logger.error("[conversationServiceEventHandler] impossible to fetch conversation " + stringifyError(error));
                        reject();
                    });
            }
        }

        else if (xmlParser(stanza).find("deleted")) {
            const deleted = xmlParser(stanza).find("deleted").first();
            if (deleted && deleted.attr("id") === "all") {
                const conv = this.conversationService.getConversationFromCache(deleted.attr("with"));
                if (conv) {
                    this.logger.info("[conversationServiceEventHandler] Remove all messages for conversation " + conv.id);
                    conv.reset();
                }
            }
            resolve();
        }
        else {
            this.logger.debug("[conversationServiceEventHandler] ignored message stanza");
            reject();
        }
    }

    private handleStandardMsg(stanzaData: any, stanzaElem: XMLElement, from: string, msgId: MessageID, resolve: any): any {

        stanzaData.outgoingMessage = false;
        stanzaData.body = stanzaElem.find("body").text();
        stanzaData.messageId = (msgId) ? msgId : stanzaElem.attr("id");
        stanzaData.carbon = false;
        stanzaData.answeredMsg = stanzaElem.find("answeredMsg");

        this.logger.debug(`[conversationServiceEventHandler] > handleStandardMsg ${stanzaData.messageId}`);

        let conversationId = this.xmppService.getBareJidFromJid(from);
        const resource = this.xmppService.getResourceFromJid(from);
        const delay = stanzaElem.find("delay"); // To check today

        // Handle message urgency
        stanzaData.urgency = 'std';
        const headersElem = stanzaElem.find("headers[xmlns='http://jabber.org/protocol/shim']");
        if (headersElem?.length) {
            const urgencyElem = headersElem.find("header[name='Urgency']");
            if (urgencyElem?.length) {
                stanzaData.urgency = urgencyElem.text();
            }
            if (stanzaData.urgency === 'undefined') { stanzaData.urgency = 'std'; }
        }

        // Handle FileSharing
        const oobElem = stanzaElem.find("x[xmlns='jabber:x:oob']");
        if (oobElem?.length) {
            const url = oobElem.find("url").text();
            const mime = oobElem.find("mime").text();
            const filename = oobElem.find("filename").text();
            const filesize = oobElem.find("size").text();
            stanzaData.oob = { url: url, mime: mime, filename: filename, filesize: filesize };
        }

        // Handle geolocalisation
        const geolocElem = stanzaElem.find("geoloc");
        if (geolocElem?.length) {
            const lon = geolocElem.find("lon").text();
            const lat = geolocElem.find("lat").text();
            stanzaData.geoloc = { lon: lon, lat: lat };
        }

        // Handle VoiceMessage
        const voiceMessage = stanzaElem.find("voicemessage");
        if (voiceMessage?.length) { stanzaData.voiceMessage = true; }

        // Handle Conference
        const conferenceElem = stanzaElem.find("x[xmlns='jabber:x:audioconference']");
        if (conferenceElem?.length) {
            const message = conferenceElem.attr("message");
            const confendpointid = conferenceElem.attr("confendpointid");
            const jid = conferenceElem.attr("jid");
            const type = conferenceElem.attr("type");
            stanzaData.conference = { type: type, message: message, confendpointid: confendpointid, roomjid: jid, ownerjid: conversationId };

            // We really just want to show the message when it is a reminder
            if (type === "reminder") {
                conversationId = jid;
            } else {
                this.logger.debug("[conversationServiceEventHandler] ignored message stanza for conference");
                resolve(stanzaData);
                return null;
            }
        }

        // Handle urlMetadata
        const urlMetadataElems = stanzaElem.find("url-metadata");
        stanzaData.urlMetadata = [];
        if (urlMetadataElems?.length) {
            urlMetadataElems.forEach((data: XMLElement) => {
                stanzaData.urlMetadata.push(UrlMetaData.create({ title: data.attr('title'), url: data.attr('url'), description: data.attr('description'), image: data.attr('image'), urlOnly: data.attr('urlOnly') as `${boolean}` }));
            });
        }

        // Handle message reactions
        const reactionElem = stanzaElem.find("reaction");
        const reactingParticipant = from && from.indexOf('room_') === 0 ? from.split('/')[1] : this.xmppService.getBareJidFromJid(from);
        if (reactionElem?.length && reactingParticipant?.length) {
            // const msg_ts = reactionElem.attr("msg_ts");
            // const msg_sender = reactionElem.attr("msg_sender");
            stanzaData.messageId = reactionElem.attr("msg_id");
            const reactionEmoji = reactionElem.text();
            const reaction = Reaction.create(reactingParticipant, reactionEmoji);
            stanzaData.reaction = reaction;
        }

        // Handle pin
        const pinElem = stanzaElem.find("pin");
        if (pinElem?.length) {
            stanzaData.pin = null;
            if (pinElem.attr("action") === "create") {
                const pinContent = stanzaElem.find("content");
                const content = JSON.parse(pinContent.first().text());
                stanzaData.pin = content;
            }
        }

        // Handle share recap
        const recapElem = stanzaElem.find("recap");
        if (recapElem?.length) {
            stanzaData.recap = null;
            if (recapElem.attr("action") === "create") {
                //get the owner id and the recap id
                const recapId = recapElem.attr("id");
                const ownerId = recapElem.attr("ownerId");
                stanzaData.recap = { id: recapId, ownerId };
            }
        }

        // Handle forward
        if (stanzaData.isForwarded) {
            const showIdentityElems = stanzaElem.find('show-identity');
            stanzaData.showIdentity = showIdentityElems.length > 0;
        }

        const replace = stanzaElem.find("replace");
        if (replace?.length) {
            stanzaData.replace = { id: replace.attr("id") };
        }

        const modified = stanzaElem.find("modified");
        const deleted = stanzaElem.find("deleted");
        stanzaData.modified = (modified && modified.length > 0) || (deleted && deleted.length > 0);

        //if meesages is deleted, remove the body in case there's something inside
        if (deleted?.length) { stanzaData.body = ''; }

        const mentionElem = stanzaElem.find("mention[xmlns='urn:xmpp:attention:0']");
        if (mentionElem.length) {
            const result = this.conversationService.processStanzaMentions(stanzaData.body, mentionElem);
            stanzaData.body = result.body;
            stanzaData.mentions = result.mentions;
        }

        // Handle Markdown and other content type
        stanzaData.additionalContent = null;
        const contents = stanzaElem.find("content[xmlns='urn:xmpp:content']");
        if (contents && contents.length > 0) {
            const contentElem = contents.first();
            stanzaData.additionalContent = MessageAdditionalContent.create(contentElem.attr('type'), contentElem.text());
        }

        // Handle subject
        const subject = stanzaElem.find("subject");
        if (subject && subject.text() !== "") { stanzaData.subject = subject.text(); }

        // Compute conversation status
        stanzaData.status = null;
        const composing = stanzaElem.find("composing");
        const paused = stanzaElem.find("paused");
        const active = stanzaElem.find("active");
        const inactive = stanzaElem.find("inactive");
        if (composing.length) { stanzaData.status = "composing"; }
        else if (paused.length) { stanzaData.status = "paused"; }
        else if (active.length) { stanzaData.status = "active"; }
        else if (inactive.length) { stanzaData.status = "inactive"; }

        return { conversationId: conversationId, resource: resource, delay: delay };
    }

    private handleCarbonMsg(carbonStanzaElem: XMLElements, stanzaData: any, stanzaElem: XMLElement, origMsgId: MessageID): any {

        const from = this.xmppService.getBareJidFromJid(carbonStanzaElem.attr("from"));
        const fromResource = this.xmppService.getResourceFromJid(carbonStanzaElem.attr("from"));
        const to = this.xmppService.getBareJidFromJid(carbonStanzaElem.attr("to"));
        const toResource = this.xmppService.getResourceFromJid(carbonStanzaElem.attr("to"));

        stanzaData.outgoingMessage = this.contactService.isUserContactJid(from);
        stanzaData.body = carbonStanzaElem.find("body").text();
        stanzaData.participant = stanzaData.outgoingMessage ? this.contactService.userContact : null;
        stanzaData.messageId = (origMsgId) ? origMsgId : carbonStanzaElem.attr("id");
        stanzaData.carbon = true;
        stanzaData.answeredMsg = carbonStanzaElem.find("answeredMsg");

        const conversationId = stanzaData.outgoingMessage ? to : from;
        const resource = stanzaData.outgoingMessage ? toResource : fromResource;
        const delay = carbonStanzaElem.find("delay"); // To check today

        // Handle Urgency
        stanzaData.urgency = 'std';
        const headersElem = carbonStanzaElem.find("headers[xmlns='http://jabber.org/protocol/shim']");
        if (headersElem?.length) {
            const urgencyElem = headersElem.find("header[name='Urgency']");
            if (urgencyElem?.length) {
                stanzaData.urgency = urgencyElem.text();
            }
        }

        // Handle FileSharing
        const oobElem = carbonStanzaElem.find("x[xmlns='jabber:x:oob']");
        if (oobElem?.length) {
            const url = oobElem.find("url").text();
            const mime = oobElem.find("mime").text();
            const filename = oobElem.find("filename").text();
            const filesize = oobElem.find("size").text();
            stanzaData.oob = { url: url, mime: mime, filename: filename, filesize: filesize };
        }

        // Handle Conference
        const conferenceElem = stanzaElem.find("x[xmlns='jabber:x:audioconference']");
        if (conferenceElem?.length) {
            const subject = conferenceElem.attr("subject");
            const confendpointid = conferenceElem.attr("confendpointid");
            const jid = conferenceElem.attr("jid");
            const type = conferenceElem.attr("type");
            stanzaData.conference = { type: type, subject: subject, confendpointid: confendpointid, roomjid: jid, ownerjid: conversationId };
        }

        // Handle urlMetadata
        const urlMetadataElems = stanzaElem.find("url-metadata");
        stanzaData.urlMetadata = [];
        if (urlMetadataElems) {
            urlMetadataElems.forEach((data: XMLElement) => {
                stanzaData.urlMetadata.push(UrlMetaData.create({ title: data.attr('title'), url: data.attr('url'), description: data.attr('description'), image: data.attr('image'), urlOnly: data.attr('urlOnly') as 'true' }));
            });
        }

        // Handle message reactions
        const reactionElem = stanzaElem.find("reaction");
        const reactingParticipant = from && from.indexOf('room_') === 0 ? from.split('/')[1] : this.xmppService.getBareJidFromJid(from);
        if (reactionElem?.length && reactingParticipant?.length) {
            // const msg_ts = reactionElem.attr("msg_ts");
            // const msg_sender = reactionElem.attr("msg_sender");
            stanzaData.messageId = reactionElem.attr("msg_id");
            const reactionEmoji = reactionElem.text();
            const reaction = Reaction.create(reactingParticipant, reactionEmoji);
            stanzaData.reaction = reaction;
        }

        const replace = carbonStanzaElem.find("replace");
        if (replace?.length) {
            stanzaData.replace = { id: replace.attr("id") };
        }

        // Handle modification or deletion
        const modified = stanzaElem.find("modified");
        const deleted = stanzaElem.find("deleted");
        stanzaData.modified = (modified && modified.length > 0) || (deleted && deleted.length > 0);

        //if meesages is deleted, remove the body in case there's something inside
        if (deleted?.length) { stanzaData.body = ''; }

        const mentionElem = carbonStanzaElem.find("mention[xmlns='urn:xmpp:attention:0']");
        if (mentionElem.length) {
            const result = this.conversationService.processStanzaMentions(stanzaData.body, mentionElem);
            stanzaData.body = result.body;
            stanzaData.mentions = result.mentions;
        }

        // Handle Markdown and other content type
        stanzaData.additionalContent = null;
        const contents = stanzaElem.find("content[xmlns='urn:xmpp:content']");
        if (contents && contents.length > 0) {
            const contentElem = contents.first();
            stanzaData.additionalContent = MessageAdditionalContent.create(contentElem.attr('type'), contentElem.text());
        }

        // Compute conversation status
        stanzaData.status = null;
        const composing = stanzaElem.find("composing");
        const paused = stanzaElem.find("paused");
        const active = stanzaElem.find("active");
        const inactive = stanzaElem.find("inactive");
        if (composing.length) { stanzaData.status = "composing"; }
        else if (paused.length) { stanzaData.status = "paused"; }
        else if (active.length) { stanzaData.status = "active"; }
        else if (inactive.length) { stanzaData.status = "inactive"; }

        // Handle geoloc
        const geolocElem = carbonStanzaElem.find("geoloc");
        if (geolocElem?.length) {
            const lon = geolocElem.find("lon").text();
            const lat = geolocElem.find("lat").text();
            stanzaData.geoloc = { lon: lon, lat: lat };
        }

        // Handle VoiceMessage
        const voiceMessage = carbonStanzaElem.find("voicemessage");
        if (voiceMessage?.length) {
            stanzaData.voiceMessage = true;
        }

        // Handle pin
        const pinElem = stanzaElem.find("pin");
        if (pinElem?.length) {
            stanzaData.pin = null;
            if (pinElem.attr("action") === "create") {
                const pinContent = stanzaElem.find("content");
                const content = JSON.parse(pinContent.first().text());
                stanzaData.pin = content;
            }
        }

        // Handle share recap
        const recapElem = stanzaElem.find("recap");
        if (recapElem?.length) {
            stanzaData.recap = null;
            if (recapElem.attr("action") === "create") {
                //get the owner id and the recap id
                const recapId = recapElem.attr("id");
                const ownerId = recapElem.attr("ownerId");
                stanzaData.recap = { id: recapId, ownerId };
            }
        }

        return { conversationId: conversationId, resource: resource, delay: delay };
    }

    public extractWebrtcStanzaData(stanza: any): Promise<any> {
        return new Promise((resolve, reject) => {
            this.logger.debug("[conversationServiceEventHandler] extractWebrtcStanzaData");

            // Extract message sender info
            const stanzaData: any = {};
            const stanzaElem = xmlParser(stanza);
            stanzaData.messageId = stanzaElem.attr("id");
            stanzaData.callerJid = stanzaElem.find("caller").text();
            stanzaData.calleeJid = stanzaElem.find("callee").text();
            stanzaData.state = stanzaElem.find("state").text();

            const callLogElem: any = stanzaElem.find("call_log");
            if (callLogElem.length) {
                const service: string = callLogElem.attr("service");
                if (service === "conference" && stanzaData.callerJid === this.contactService.userContact.jid) {
                    // I am the conference initiator: we ignore the event, because there is no conversation on which
                    // we can send a message for this call
                    // Reminder:
                    // For a pbx conference call_log:
                    // - callerJid always contains the conference initiator jid (on all participants)
                    // - on the conference initiator, we have callerJid = calleeJid = userContact.jid
                    // - on each conference slave participant, callerJid is the conference initiator's jid and calleeJid is the slave participant's jid
                    this.logger.debug("[conversationServiceEventHandler] extractWebrtcStanzaData -- ignore pbx conference initiator message");
                    resolve(null);
                    return;
                }
            }

            // Extract conversationId
            let conversationId = null;
            if (!this.contactService.isUserContactJid(stanzaData.callerJid)) { conversationId = stanzaData.callerJid; }
            else {
                conversationId = stanzaData.calleeJid;
                stanzaData.participant = this.contactService.userContact;
            }

            // Ignore Janus messages
            if (conversationId.indexOf("janusgateway") !== -1) {
                this.logger.debug("[conversationServiceEventHandler] extractWebrtcStanzaData -- ignore Janus message");
                reject();
                return;
            }

            // Ignore MediaPillar messages
            if (this.webrtcGatewayService?.isMediaPillarJid(stanzaData.callerJid)) {
                this.logger.debug("[conversationServiceEventHandler] extractWebrtcStanzaData -- ignore MediaPillar message");
                resolve(null);
                return;
            }

            // Compute call duration
            let duration = 0;
            if (stanzaElem.find("duration")) { duration = parseInt(stanzaElem.find("duration").text(), 10); }
            if (duration > 0) {
                const durationObject = intervalToDuration({ start: 0, end: duration });
                const formattedDuration = formatDuration(durationObject, { format: ['hours', 'minutes', 'seconds'], delimiter: ' ', zero: true, locale: enUS });

                const currentLanguage = this.settingsService?.getAppliLanguage();
                if (currentLanguage && currentLanguage.code === "de") {
                    stanzaData.duration = `(${formattedDuration.replace(' hours', ' H').replace(' minutes', ' min').replace(' minute', ' min').replace(' seconds', ' s').replace(' second', ' s')})`;
                }
                else {
                    stanzaData.duration = `(${formattedDuration.replace(' hours', 'H').replace(' hour', 'H').replace(' minutes', 'm').replace(' minute', 'm').replace(' seconds', 's').replace(' second', 's')})`;
                }
            }
            else { stanzaData.duration = 0; }

            // Compute call date
            const date = stanzaElem.find("date").text();
            if (date) { stanzaData.date = new Date(date); }

            // Fill body
            stanzaData.body = "";
            if (stanzaData.state === "missed") { stanzaData.body = "missedCall||" + stanzaData.date; }
            else if (stanzaData.state === "answered") { stanzaData.body = "activeCallMsg||" + stanzaData.date + "||" + stanzaData.duration; }

            // Get the associated conversation
            this.conversationService.getOrCreateConversation(conversationId, 'contact')
                .then((conversation: ConversationRB) => {
                    stanzaData.conversation = conversation;
                    if (!stanzaData.participant) { stanzaData.participant = conversation.contact; }
                    resolve(stanzaData);
                })
                .catch((error) => {
                    this.logger.error("[conversationServiceEventHandler] impossible to fetch conversation " + error.message);
                    reject();
                });
        });
    }

    public async handleChatMessage(stanzaData: any): Promise<void> {
        // Create the chat message
        this.logger.info("[ConversationServiceEventHandler] handleChatMessage");

        const body = stanzaData.body;
        let message: Message = null;
        const conversation: ConversationRB = stanzaData.conversation;

        if (conversation?.bubble) { conversation.bubble.lastActivityDate = new Date(); }

        if (stanzaData.oob && this.fileStorageService && !stanzaData.modified) {
            const url = stanzaData.oob.url;
            const fileName = stanzaData.oob.filename;
            const fileId = this.fileStorageService.extractFileIdFromUrl(url);

            message = this.conversationService.addFileSharingMessage(conversation, stanzaData.participant, stanzaData.date, stanzaData.body, stanzaData.messageId, fileId,
                fileName, stanzaData.geoloc, stanzaData.urgency, stanzaData.isForwarded);

            const fd = this.fileStorageService.getFileDescriptorById(fileId);
            const shouldForceRetrieve = (fd && !fd.isAlreadyFileViewer(conversation.id));

            try {
                const fileDescriptor = await this.fileStorageService.retrieveAndStoreOneFileDescriptor(fileId, shouldForceRetrieve);
                fileDescriptor.previewBlob = await this.fileServerService.getBlobThumbnailFromFileDescriptor(fileDescriptor);
                fileDescriptor.previewBlobSubject.next(fileDescriptor.previewBlob);
                this.eventService.publish("ON_FILE_DESCRIPTOR_RECEIVED_EVENT", { fileId: fileDescriptor.id });
            }
            catch (error) {
                this.logger.info("[ConversationServiceEventHandler] handleChatMessage error " + error);
            }
        }
        else if (stanzaData.replace) {
            message = conversation.getMessageById(stanzaData.replace.id);
            if (message && stanzaData.participant.jid !== message.from.jid) {
                this.logger.warn("[ConversationServiceEventHandler] handleChatMessage : message Replaced by somebody else - Skip");
                message = null;
            }
            else {
                if (!message) {
                    //create the initial replaced message
                    message = this.conversationService.addChatMessage(conversation, stanzaData.participant, stanzaData.date, "", stanzaData.replace.id, null,
                        stanzaData.subject, null, null, stanzaData.additionalContent, null, null, stanzaData.isForwarded);
                }
                message.addReplaceMsg(stanzaData.messageId, body);
                message.mentions = stanzaData.mentions;
                conversation.updateAfterReplaceEvent(message);
            }
        }
        else if (stanzaData.modified) {
            message = conversation.getMessageById(stanzaData.messageId);
            if (message) {
                message.modify(stanzaData);
                conversation.updateAfterReplaceEvent(message);
                const rbEvt = RBEvent.create(ConversationServiceEvents.ON_MESSAGE_MODIFIED_IN_CONVERSATION, { conversation, message });
                this.conversationService.publish(rbEvt);
            }
        }
        else if (body?.length > 0 || stanzaData.additionalContent !== null) {
            const answeredMsgId = (stanzaData.answeredMsg) ? stanzaData.answeredMsg.text() : null;
            const answeredMsgDate = (stanzaData.answeredMsg) ? stanzaData.answeredMsg.attr("stamp") : null;

            message = this.conversationService.addChatMessage(conversation, stanzaData.participant, stanzaData.date, stanzaData.body, stanzaData.messageId, null,
                stanzaData.subject, answeredMsgId, answeredMsgDate, stanzaData.additionalContent, stanzaData.mentions, stanzaData.urgency, stanzaData.isForwarded);

            if (answeredMsgId) {
                const answeredMessage: Message = conversation.getMessageById(answeredMsgId);
                const isFromMe = this.contactService.isUserContact(stanzaData.participant);
                if (answeredMessage && isFromMe) {
                    answeredMessage.urgencyAck = true;
                    if (answeredMessage.urgencyHandler) { answeredMessage.urgencyHandler(); }
                } else if (!answeredMessage) {
                    this.logger.error("[ConversationServiceEventHandler] Not possible to handle answeredMsg because replied msg not found");
                }
            }
        } else if (stanzaData.reaction) {
            message = conversation.getMessageById(stanzaData.messageId);
            if (message) {
                message.updateReaction(stanzaData.reaction);
            }

        }

        if (message) {
            if (!message.isForwarded) { // Only change forwarded info of message if this was not set before as there is no way a message that is forwarded can become "not forwarded" over time
                message.isForwarded = stanzaData.fwdMsgdInfo !== undefined;
                if (message.isForwarded) {
                    message.forwardedInfo = stanzaData.fwdMsgdInfo;
                    message.showIdentity = stanzaData.fwdMsgdInfo.showIdentity;
                }
            }
            message.urlMetadata = stanzaData.urlMetadata.length > 0 ? stanzaData.urlMetadata[0] : null;
            this.acknowledgeHandledChatMessage(stanzaData, conversation, message);
        }
    }

    private acknowledgeHandledChatMessage(stanzaData: any, conversation: ConversationRB, message: Message): void {
        // Create the chat message
        this.logger.info("[ConversationServiceEventHandler] acknowledgeHandledChatMessage");
        if (!message) {
            //ACK my messages inside a room (only for the device I've used to send the message)
            message = conversation.getMessageById(stanzaData.messageId);
            if (conversation.bubble && message && message.side === MessageSide.RIGHT) {
                this.conversationService.sendAckReceivedMessage(conversation, message);
            }
            return;
        }

        // Handle reactions
        if (stanzaData.reaction) {
            message.sendUpdateEvent();
            return; // We return here to avid any unnecessary actions. However, the conversation status is not changed. This should not have an impact but just in case, it might be necessary to add it here
        }

        // Handle received messages
        if (message.side === MessageSide.LEFT) {

            // check if chat is hidden, so do not read messages received in the conversation
            let checkIfChatIsHidden = false;

            //add SFU v2 check
            if (this.webConferenceService && conversation.bubble) {
                const webConferenceSession = this.webConferenceService.getWebConferenceSessionById(conversation.bubble.dbId);
                checkIfChatIsHidden = checkIfChatIsHidden || (webConferenceSession?.haveJoined && !webConferenceSession.isInExternalWindow && (!conversation.infoVisible || (conversation.infoVisible && conversation.selectedTabName !== "chat")));
            }

            checkIfChatIsHidden = checkIfChatIsHidden || (conversation.videoCall && conversation.videoCall.callStatus !== CallStatus.ENDED && !conversation.infoVisible);

            if (conversation.bubble?.isWebinarBubble() && this.webinarService) {
                checkIfChatIsHidden = (!conversation.infoVisible || (conversation.infoVisible && conversation.selectedTabName !== "chat"));
            }

            //ack-received the message and increment missed counter
            this.conversationService.sendAckReceivedMessage(conversation, message);

            //if messages is a deletion or modificiation, check the state of the last message
            //and do not increase the missed IM if we've not read it yet
            if (!stanzaData.offline && stanzaData.convReady) {
                if ((message.isModified() || message.isDeleted()) && message.receiptStatus !== MessageStatus.READ) {
                    //we have not read the message yet
                    message.sendUpdateEvent();
                }
                else {
                    conversation.setMissedCounter(conversation.missedCounter + 1);
                    this.conversationService.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_CONVERSATION_AUTO_ACK_CHECK, { conversation, message, type: "IM", checkIfChatIsHidden });
                    this.conversationService.updateMissedCounters();
                }
            }

            if (message.isModified() || message.isDeleted()) {
                message.sendUpdateEvent();
                if (message.receiptStatus === MessageStatus.READ) {
                    this.conversationService.sendAckReadOrReceivedMessage(conversation, stanzaData.messageId, 'read');
                }
            }
        }
        else {
            if (stanzaData.carbon) { message.setReceiptStatus(MessageStatus.SENT); }
            else { message.setReceiptStatus(MessageStatus.IN_PROGRESS); }
        }

        this.conversationService.setStatusMessage(conversation, stanzaData.participant, ConversationStatus.ACTIVE);
    }

    private handleReceiptReceiveMessage(conversation: ConversationRB, stanza: any) {
        this.logger.info("[ConversationServiceEventHandler] handleReceiptReceiveMessage");

        // Look for "received" child in stanza
        const stanzaElem = xmlParser(stanza);
        const recvMessage = stanzaElem.find("received[xmlns='urn:xmpp:receipts']");
        if (recvMessage.length) {

            // Get the associate message (if already available (can be not also load from mam))
            const messageId = recvMessage.attr("id") as MessageID;
            const message: Message = conversation.getMessageById(messageId);
            if (message) {
                let contactJid = stanzaElem.attr("from").split("/")[1];
                if (!contactJid) { contactJid = stanzaElem.attr("from"); }
                const event = recvMessage.attr("event");
                const maskRead = recvMessage.attr("maskread") === "true";

                // If room and message is send by me or P2P conversation
                if ((conversation.bubble && this.contactService.isUserContactJid(contactJid)) || !conversation.bubble) {
                    // Update message receiptStatus
                    if (event === "received") { message.setReceiptStatus(MessageStatus.UNREAD); }
                    // Handle missed counter
                    else if (event === "read") {
                        if (!maskRead) { message.setReceiptStatus(MessageStatus.READ); }
                        conversation.setMissedCounter(0);
                        this.conversationService.updateMissedCounters();
                    }
                }

                // If I've read the message on another device, update the missed Counter
                if (this.contactService.isUserContactJid(contactJid) && event === "read" && message.side === MessageSide.LEFT) {
                    conversation.setMissedCounter(0);
                    this.conversationService.updateMissedCounters();
                }
            }
            else {
                const temp = stanzaElem.attr("from");
                const event = recvMessage.attr("event");

                let contactJid = "";
                if (temp) { contactJid = temp.split("/")[1]; }
                if (!contactJid) { contactJid = temp; }

                if (this.contactService.isUserContactJid(contactJid) && event === "read") {
                    //message does not exist on our client, check if we've read it on another device
                    conversation.setMissedCounter(0);
                    this.conversationService.updateMissedCounters();
                    // this.eventService.publish("ON_CONVERSATIONS_UPDATED_EVENT", conversation);
                }
            }
        }
    }

    private handleStatusMessage(conversation: ConversationRB, participant: Contact, stanza: any) {
        this.logger.info("[conversationServiceEventHandler] handleStatusMessage ");
        // Handle conversation status
        let status: ConversationStatus;
        // Incoming message from Android
        const stanzaChildren = xmlParser(stanza).children();
        if (stanzaChildren.prop("tagName") === "thread") status = this.stringToStatus(stanzaChildren.elems[1].prop("tagName"));
        else status = this.stringToStatus(stanzaChildren.prop("tagName"));

        this.conversationService.setStatusMessage(conversation, participant, status);
    }

    public stringToStatus(status: string): ConversationStatus {
        switch (status) {
            case 'composing': return ConversationStatus.COMPOSING;
            case 'paused': return ConversationStatus.PAUSED;
            default: return ConversationStatus.ACTIVE;
        }
    }

    public onRecordingMessageReceived(stanza: any): boolean {
        this.logger.info("[ConversationServiceEventHandler] onRecordingMessageReceived");
        try {
            const msgContent = stanza.textContent;
            this.extractStanzaData(stanza)
                .then((stanzaData: any) => {
                    if (msgContent === "start") {
                        const conversation: ConversationRB = stanzaData.conversation;
                        this.conversationService.addRecordingMessage(conversation, stanzaData.participant, stanzaData.date, msgContent, stanzaData.messageId);
                        this.conversationService.setStatusMessage(conversation, stanzaData.participant, ConversationStatus.ACTIVE);
                        if (conversation.call) { conversation.call.isRecorded = true; }
                        // this.eventService.publish("ON_CONVERSATIONS_UPDATED_EVENT", conversation);
                        this.eventService.publish("ON_RECORDING_START_MSG_RECEIVED");
                    }
                    else if (msgContent === "stop") {
                        const conversation = stanzaData.conversation;
                        if (conversation) {
                            this.conversationService.addRecordingMessage(conversation, stanzaData.participant, stanzaData.date, msgContent, stanzaData.messageId);
                            if (conversation.call) { conversation.call.isRecorded = false; }
                        }
                        this.eventService.publish("ON_RECORDING_STOP_MSG_RECEIVED");
                    }
                    else if (msgContent === "remoteStopVideo") { this.eventService.publish("ON_RECORDING_REMOTESTOPVIDEO_MSG_RECEIVED"); }
                    else if (msgContent === "remoteChangeMediaVideo") { this.eventService.publish("ON_RECORDING_REMOTECHANGEMEDIAVIDEO_MSG_RECEIVED"); }
                    else if (msgContent === "remoteStopSharing") { this.eventService.publish("ON_RECORDING_REMOTESTOPSHARING_MSG_RECEIVED"); }
                    else if (msgContent === "remoteChangeMediaSharing") { this.eventService.publish("ON_RECORDING_REMOTECHANGEMEDIASHARING_MSG_RECEIVED"); }
                });
            return true;
        }
        catch (error) {
            this.logger.error("[ConversationServiceEventHandler] onRecordingMessageReceived error " + error);
            return true;
        }
    }

    public handleRecapShareMessage(stanzaData: any) {
        const conversation = stanzaData.conversation;
        const msgId = stanzaData.messageId;
        const contact = stanzaData.participant;

        // usefull for conversationCell preview of msg
        let translatedMessage = this.i18n.translate('shareRecapMsgRoom', { userDisplayName: contact.displayName });
        if (this.contactService.isUserContact(contact)) {
            translatedMessage = this.i18n.translate('shareRecapMsgRoomMine');
        }

        const additionalContent = MessageAdditionalContent.create("recapId", stanzaData.recap?.id);
        const message = this.conversationService.addShareRecapMessage(conversation, contact, new Date(), translatedMessage, msgId, additionalContent);
        this.conversationService.setStatusMessage(conversation, contact, ConversationStatus.ACTIVE);
        this.conversationService.sendAckReceivedMessage(conversation, message);
        this.conversationService.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_CONVERSATION_AUTO_ACK_CHECK, { conversation, message });
    }

    public handlePinMessage(stanzaData: any) {
        const conversation = stanzaData.conversation;
        const msgId = stanzaData.messageId;
        const contact = stanzaData.participant;

        // usefull for conversationCell preview of msg
        let translatedMessage = this.i18n.translate('pinMsgRoom', { userDisplayName: contact.displayName });
        if (this.contactService.isUserContact(contact)) {
            translatedMessage = this.i18n.translate('pinMsgRoomMine');
        }

        const message = this.conversationService.addPinMessage(conversation, contact, new Date(), translatedMessage, msgId);
        this.conversationService.setStatusMessage(conversation, contact, ConversationStatus.ACTIVE);
        this.conversationService.sendAckReceivedMessage(conversation, message);
        this.conversationService.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_CONVERSATION_AUTO_ACK_CHECK, { conversation, message });
    }

    public handlePollEvent(conversation: ConversationRB, contact: Contact, msgId: MessageID, pollId: string) {
        const additionalContent = MessageAdditionalContent.create("pollId", pollId);

        // usefull for conversationCell preview of msg
        let translatedMessage = this.i18n.translate('pollPublishMsgRoom', { userDisplayName: contact.displayName });
        if (this.contactService.isUserContact(contact)) {
            translatedMessage = this.i18n.translate('pollPublishMsgRoomMine');
        }

        const message = this.conversationService.addPollMessage(conversation, contact, new Date(), translatedMessage, msgId, additionalContent);
        this.conversationService.setStatusMessage(conversation, contact, ConversationStatus.ACTIVE);
        this.conversationService.sendAckReceivedMessage(conversation, message);
        this.conversationService.sendInnerEvent(ConversationServiceInnerEvents.RAINBOW_CONVERSATION_AUTO_ACK_CHECK, { conversation, message });
    }
}
